<i18n lang="yaml" locale="en">
  rename slide: Rename slide
  name: Name
  font: Font
  add font long: Add a font by dropping it here.
  add font short: Add as font.
</i18n>

<i18n lang="yaml" locale="de">
  rename slide: Seite umbenennen
  name: Name
  font: Schriftart
  add font long: Fügen Sie eine Schriftart hinzu, indem Sie sie hier ablegen.
  add font short: Als Schriftart hinzufügen.
</i18n>

<template>
  <transition
    v-if="!menu"
    :enter-active-class="enterClass"
    :leave-active-class="leaveClass"
    :duration="duration"
  >
    <keep-alive>
      <grid
        v-if="isActive"
        class="container"
        :width="width"
        :height="height"
        :format="$project.format"
        :style="gridStyle"
      >
        <component
          v-for="child in children"
          :key="child.getters.path"
          :path="child.getters.path"
          :is="child.getters.type"
          v-bind="child.getters.props"
        />
      </grid>
    </keep-alive>
  </transition>
  <div
    v-else
    :is="$editMode ? 'slide-editor' : 'div'"
    class="menu-item"
    :class="slideClasses"
    :style="slideStyle"
  >
    <div class="indicator-stack">
      <div class="indicator-container">
        <div
          class="indicator"
          :style="indicatorStyle"
        />
      </div>
    </div>
    <div
      class="title"
      :style="textStyle"
      @click="navigate($store.getters.name)"
    >
      <span>{{ title }}</span>
    </div>
  </div>
</template>

<script>
import BaseModule from '@/components/modules/BaseModule'
import Grid from '@/components/modules/Grid'
import SlideEditor from '@/components/editor/SlideEditor'
import translatable from '@/store/mixins/translatable'
import fontHandler from '@/mixins/fontHandler'

export default {
  // eslint-disable-next-line
  name: 'slide',
  extends: BaseModule,
  customStore: translatable,
  components: {
    Grid,
    SlideEditor
  },
  mixins: [fontHandler([
    'font'
  ])],
  inject: ['$project'],
  props: {
    transition: {
      type: String,
      default: 'forward'
    },
    menu: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: ''
    },
    textColor: {
      type: String,
      default: ''
    },
    indicatorColor: {
      type: String,
      default: ''
    },
    activeColor: {
      type: String,
      default: ''
    },
    navigate: {
      type: Function,
      default: () => {}
    },
    // eslint-disable-next-line
    _headlineRenameSlide: {
      input: 'heading',
      label: 'rename slide'
    },
    title: {
      type: String,
      required: true,
      input: 'text',
      label: 'name'
    },
    // eslint-disable-next-line
    _headlineFont: {
      input: 'heading',
      label: 'font'
    },
    font: {
      input: 'drop',
      type: String,
      contentType: 'font',
      placeholder: 'add font long',
      tooltip: 'add font short',
      default: ''
    }
  },
  data () {
    return {
      cachedPath: ''
    }
  },
  computed: {
    width () {
      return this.$project.format === 'landscape' ? 192 : 108
    },
    height () {
      return this.$project.format === 'landscape' ? 108 : 192
    },
    slideClasses () {
      return {
        active: this.isActive
      }
    },
    slideStyle () {
      return {
        'background-color': this.$props.backgroundColor
      }
    },
    gridStyle () {
      return {
        ...this.fontStyles.font
      }
    },
    indicatorStyle () {
      return {
        'background-color': this.isActive ? this.$props.activeColor : this.$props.indicatorColor
      }
    },
    textStyle () {
      return {
        color: this.isActive ? this.$props.activeColor : this.$props.textColor
      }
    },
    enterClass () {
      switch (this.transition) {
        case 'forward':
          return 'slideInRight'
        case 'backward':
          return 'slideInLeft'
        default:
          return ''
      }
    },
    leaveClass () {
      switch (this.transition) {
        case 'forward':
          return 'slideOutLeft'
        case 'backward':
          return 'slideOutRight'
        default:
          return ''
      }
    },
    duration () {
      switch (this.transition) {
        case 'forward':
        case 'backward':
          return undefined
        case 'none':
          return 0 // TODO: this doesn't work yet. Delete a slide and the slide will be shown with the other slide on top still for ~750ms...
        default:
          return 750
      }
    }
  },
  created () {
    // the menu slide gets created a lot more often than the regular slide
    if (!this.menu) {
      this.$root.$store.dispatch('registerLink', {
        group: 'slide',
        href: this.$store.getters.path,
        titleFunc: () => this.title,
        action: () => { this.navigate(this.$store.getters.name) }
      })
      // cache the path, because it is not available in the beforeDestroy hook anymore
      this.cachedPath = this.$store.getters.path
    }
  },
  beforeDestroy () {
    // the menu slide gets created a lot more often than the regular slide
    if (!this.menu) {
      this.$root.$store.dispatch('unregisterLink', {
        group: 'slide',
        href: this.cachedPath
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  position: absolute;
  width: 100%;
  height: 100%;
}

.menu-item {
  flex: auto;
  position: relative;

  .indicator-stack {
    height: 1px;

    .indicator-container {
      height: 100%;
      overflow: hidden;

      .indicator {
        height: 100%;
      }
    }
  }

  &.active {
    .indicator-stack {
      height: 3px;
      margin-bottom: -2px;
    }
  }

  .title {
    height: 100%;
    font-weight: 500;
    letter-spacing: 0.01em;
    padding: 1vmin 1.5vmin;
  }
}
</style>
